import React from 'react';
import { 
  Box, 
  Flex, 
  Text, 
  VStack, 
  Circle, 
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCheck, FaUser, FaStore, FaPercent } from 'react-icons/fa';
import { keyframes } from '@emotion/react';

interface Step {
  number: number;
  title: string;
  description: string;
  icon?: React.ElementType;
}

interface StepProgressProps {
  steps: Step[];
  currentStep: number;
}

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

export function StepProgress({ steps, currentStep }: StepProgressProps) {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const completedColor = 'green.500';
  const currentColor = 'blue.500';
  const upcomingColor = useColorModeValue('gray.300', 'gray.600');

  const getStepStatus = (stepNumber: number) => {
    if (stepNumber < currentStep) return 'completed';
    if (stepNumber === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepNumber: number) => {
    switch (stepNumber) {
      case 1: return FaUser;
      case 2: return FaStore;
      case 3: return FaPercent;
      default: return FaUser;
    }
  };

  return (
    <Box 
      w="full" 
      maxW="4xl" 
      mx="auto" 
      p={6}
      bg={bgColor}
      borderRadius="xl"
      boxShadow="lg"
      border="1px solid"
      borderColor={borderColor}
    >
      {/* Mobile View */}
      <Box display={{ base: 'block', md: 'none' }}>
        <VStack spacing={6} align="stretch">
          {steps.map((step, index) => {
            const status = getStepStatus(step.number);
            const isLast = index === steps.length - 1;
            const IconComponent = getStepIcon(step.number);
            
            return (
              <Box key={step.number} w="full">
                <Flex align="center" gap={4}>
                  <Circle
                    size="50px"
                    bg={status === 'completed' ? completedColor : 
                        status === 'current' ? currentColor : upcomingColor}
                    color="white"
                    fontWeight="bold"
                    fontSize="lg"
                    position="relative"
                    animation={status === 'current' ? `${pulseAnimation} 2s infinite` : 'none'}
                    boxShadow={status === 'current' ? '0 0 20px rgba(66, 153, 225, 0.5)' : 'none'}
                  >
                    {status === 'completed' ? (
                      <Icon as={FaCheck} boxSize={5} />
                    ) : (
                      <Icon as={IconComponent} boxSize={5} />
                    )}
                  </Circle>
                  
                  <Box ml={4} flex={1}>
                    <Text
                      fontSize="lg"
                      fontWeight="bold"
                      color={status === 'completed' ? 'green.600' : 
                             status === 'current' ? 'blue.600' : 'gray.400'}
                      mb={1}
                    >
                      {step.title}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={status === 'upcoming' ? 'gray.400' : 'gray.600'}
                      lineHeight="short"
                    >
                      {step.description}
                    </Text>
                  </Box>
                </Flex>
                
                {!isLast && (
                  <Box
                    ml="25px"
                    mt={4}
                    mb={2}
                    h="32px"
                    w="2px"
                    bg={step.number < currentStep ? completedColor : borderColor}
                    borderRadius="full"
                  />
                )}
              </Box>
            );
          })}
        </VStack>
      </Box>

      {/* Desktop View */}
      <Box display={{ base: 'none', md: 'block' }}>
        <Flex align="center" justify="center" position="relative">
          {steps.map((step, index) => {
            const status = getStepStatus(step.number);
            const isLast = index === steps.length - 1;
            const IconComponent = getStepIcon(step.number);
            
            return (
              <React.Fragment key={step.number}>
                <VStack flex={1} align="center" spacing={3}>
                  <Circle
                    size="60px"
                    bg={status === 'completed' ? completedColor : 
                        status === 'current' ? currentColor : upcomingColor}
                    color="white"
                    fontWeight="bold"
                    fontSize="xl"
                    position="relative"
                    animation={status === 'current' ? `${pulseAnimation} 2s infinite` : 'none'}
                    boxShadow={status === 'current' ? '0 0 25px rgba(66, 153, 225, 0.6)' : 'lg'}
                    transition="all 0.3s ease"
                    _hover={{ transform: 'scale(1.05)' }}
                  >
                    {status === 'completed' ? (
                      <Icon as={FaCheck} boxSize={6} />
                    ) : (
                      <Icon as={IconComponent} boxSize={6} />
                    )}
                  </Circle>
                  
                  <VStack spacing={1} textAlign="center" maxW="200px">
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={status === 'completed' ? 'green.600' : 
                             status === 'current' ? 'blue.600' : 'gray.400'}
                      lineHeight="short"
                    >
                      {step.title}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={status === 'upcoming' ? 'gray.400' : 'gray.600'}
                      lineHeight="short"
                    >
                      {step.description}
                    </Text>
                  </VStack>
                </VStack>
                
                {!isLast && (
                  <Box
                    h="3px"
                    w="100px"
                    mx={4}
                    mt="-30px"
                    bg={step.number < currentStep ? completedColor : borderColor}
                    borderRadius="full"
                    position="relative"
                    _before={{
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      w: '8px',
                      h: '8px',
                      borderRadius: 'full',
                      bg: step.number < currentStep ? completedColor : borderColor,
                      boxShadow: '0 0 10px rgba(0,0,0,0.1)'
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Flex>
      </Box>
    </Box>
  );
} 